import handleErrorAndDisplay from './handleErrorAndDisplay';
import { hasDetailMessages, transformAndDisplay } from './transformErrorResponse';
import { __ } from '@wordpress/i18n';

/**
 * Display network error to user with proper error format handling
 *
 * @param {Error} error - Error object from network request
 * @param {object} options - Options object
 * @param {boolean} options.silent - If true, suppress error display
 * @returns {void}
 */
export const displayNetworkError = (error, options = {}) => {
    if (options.silent) return;

    // Silently ignore 403 errors that were handled with redirect
    if (error?.handled) {
        return;
    }

    try {
        const errorData = error?.data || error?.response?.data;

        // Priority 1: Pydantic validation errors
        if (errorData && hasDetailMessages(errorData)) {
            transformAndDisplay(handleErrorAndDisplay, errorData);
            return;
        }

        // Priority 2: String detail property
        if (errorData?.detail && typeof errorData.detail === 'string') {
            handleErrorAndDisplay(errorData.detail);
            return;
        }

        // Priority 3: Error message (skip generic 'Request failed')
        if (error?.message && error.message !== 'Request failed') {
            handleErrorAndDisplay(error.message);
            return;
        }

        // Priority 4: Status-based messages
        if (error?.status) {
            const statusMessages = {
                400: __('Bad request. Please check your input.', 'soundbetween'),
                401: __('Unauthorized. Please login again.', 'soundbetween'),
                403: __('Access denied.', 'soundbetween'),
                404: __('Resource not found.', 'soundbetween'),
                409: __('Conflict. The resource already exists.', 'soundbetween'),
                422: __('Validation error. Please check your input.', 'soundbetween'),
                500: __('Server error. Please try again later.', 'soundbetween'),
                502: __('Bad gateway. Server is temporarily unavailable.', 'soundbetween'),
                503: __('Service unavailable. Please try again later.', 'soundbetween'),
            };

            handleErrorAndDisplay(statusMessages[error.status] ||
                __('An error occurred. Please try again.', 'soundbetween'));
            return;
        }

        // Priority 5: Generic fallback
        handleErrorAndDisplay(__('An unexpected error occurred.', 'soundbetween'));

    } catch (displayError) {
        console.error('Error displaying error:', displayError);
        handleErrorAndDisplay(__('An error occurred.', 'soundbetween'));
    }
};

export default displayNetworkError;
